import React from 'react';
import { API } from "aws-amplify";
import { toast } from "react-toastify";

import InputCell from "@/common/form/InputCell";
import ConfirmModal from "@/common/ConfirmModal";
import FileDownload from "@/common/FileDownload";
import CachedIcon from '@mui/icons-material/Cached';
import GetAppIcon from '@mui/icons-material/GetApp';
import FileCopyIcon from '@mui/icons-material/FileCopy';

const MAX_SURVEY_NAME_LENGTH = 255;
const MAX_START_WORD_LENGTH  = 64;

export default function AddEditSurvey(props) {
    var { onCancel, onSuccess, existingSurvey } = props;

    // ---------- existing state ----------
    const [isPublic, setIsPublic] = React.useState(existingSurvey ? existingSurvey.public : false);
    const [surveyName, setSurveyName] = React.useState(existingSurvey ? existingSurvey.name : '');

    // ---------- NEW (EN-19608) state ----------
    const [qrEnabled, setQrEnabled] = React.useState(existingSurvey ? !!existingSurvey.qrEnabled : false);
    const [startWord, setStartWord] = React.useState(existingSurvey && existingSurvey.startWord ? existingSurvey.startWord : '');
    const [qrToken, setQrToken] = React.useState(existingSurvey && existingSurvey.qrToken ? existingSurvey.qrToken : '');

    // Ensure the focused element is fully visible inside the scrollable .content area,
    // accounting for the 56px action bar at the bottom.
    // When scrolling up, include the parent .tableSection so the section label stays visible.
    // Deferred so it runs after the browser's own native focus-scroll.
    const scrollIntoViewInsideModal = (e) => {
        const el = e.currentTarget;
        setTimeout(() => {
            const container = el.closest('.content');
            if (!container) return;
            const elRect = el.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const visibleBottom = containerRect.bottom - 56; // 56px action bar overlaps bottom
            if (elRect.bottom > visibleBottom) {
                container.scrollTop += elRect.bottom - visibleBottom + 8;
            } else {
                // For upward scroll, use the tableSection top so the section label is included
                const section = el.closest('.tableSection');
                const topRect = section ? section.getBoundingClientRect() : elRect;
                if (topRect.top < containerRect.top) {
                    container.scrollTop -= containerRect.top - topRect.top + 8;
                }
            }
        }, 0);
    };

    // ---------- derived ----------
    let surveyUrl = null;
    if (existingSurvey) {
        surveyUrl = window.location.protocol + "//" + window.location.hostname + "/take-survey/public/" + existingSurvey.encryptedId;
    }

    // Regenerate / Download are only meaningful for an existing, already-QR-enabled survey.
    // (FE will never call `/qrcode` before the backend has a qr_token persisted.)
    const canUseQrActions = !!(existingSurvey && existingSurvey.id && existingSurvey.qrEnabled);

    // ---------- handlers ----------

    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(surveyUrl);
            toast("URL copied to clipboard", { position: "top-center", type: "success" });
        } catch (e) {
            toast("Unable to copy URL", { position: "top-center", type: "error" });
        }
    };

    const handleQrToggle = () => {
        const turningOff = qrEnabled;
        setQrEnabled(!qrEnabled);
        if (turningOff) {
            // Restore to the DB-saved start word and token when toggling OFF.
            setStartWord(existingSurvey && existingSurvey.startWord ? existingSurvey.startWord : '');
            setQrToken(existingSurvey && existingSurvey.qrToken ? existingSurvey.qrToken : '');
        }
    };

    const handleStartWordChange = (e) => {
        setStartWord(e.target.value);
    };

    const handleQrTokenChange = (e) => {
        setQrToken(e.target.value);
    };

    const handleRegenerate = () => {
        if (!existingSurvey || !existingSurvey.id) return;
        API.post('Core', `/api/v1/survey/${existingSurvey.id}/qrcode/regenerate`)
            .then(
                response => {
                    toast("QR code regenerated", { position: "top-center", type: "success" });
                    // Update the token field to show the newly generated token.
                    if (response && response.qrToken) {
                        setQrToken(response.qrToken);
                    }
                    // Let the parent re-hydrate so subsequent downloads get the new token.
                    onSuccess(response);
                },
                error => {
                    const msg = error && error.response && error.response.data && error.response.data.message;
                    toast("Could not regenerate QR code" + (msg ? ": " + msg : ""), { position: "top-center", type: "error" });
                }
            );
    };

    const saveSurvey = async () => {
        const nameVal = surveyName.trim();
        if (nameVal.length === 0) {
            toast("Survey name is required", { position: "top-center", type: "error" });
            return;
        }
        if (nameVal.length > MAX_SURVEY_NAME_LENGTH) {
            toast(`Survey title limit is ${MAX_SURVEY_NAME_LENGTH} characters`, { position: "top-center", type: "error" });
            return;
        }

        if (qrEnabled) {
            const trimmedWord = (startWord || '').trim();
            if (!trimmedWord) {
                toast("Start word is required when QR Code is enabled", { position: "top-center", type: "error" });
                return;
            }
            if (trimmedWord.length > MAX_START_WORD_LENGTH) {
                toast(`Start word must be ${MAX_START_WORD_LENGTH} characters or less`, { position: "top-center", type: "error" });
                return;
            }
        }

        const trimmedToken = (qrToken || '').trim();

        const body = {
            name: nameVal,
            public: isPublic,
            qrEnabled: qrEnabled,
            startWord: qrEnabled ? (startWord || '').trim() : null,
            qrToken: qrEnabled ? (trimmedToken || null) : null
        };

        try {
            const response = existingSurvey
                ? await API.put('Core', '/api/v1/survey/' + existingSurvey.id, { body })
                : await API.post('Core', '/api/v1/survey', { body });
            toast("Survey saved!", { position: "top-center", type: "success" });
            onSuccess(response);
        } catch (error) {
            const msg = error?.response?.data?.message || 'Unknown error';
            const action = existingSurvey ? 'update' : 'create';
            toast(`Could not ${action} survey: ${msg}`, { position: "top-center", type: "error" });
        }
    };

    // ---------- render ----------

    const isDoneDisabled = !surveyName.trim() || (qrEnabled && !(startWord || '').trim());

    return (
        <div>
            <div className="content">
                <div className="tableView">
                    {/* ---------- Survey Name (existing) ---------- */}
                    <div className="tableSection sectionName">
                        <p style={{ fontSize: '0.75em', color: 'var(--colorTitle)', marginBottom: '6px', marginTop: '-4px' }}>
                            Fields marked with <span style={{ color: 'red' }}>*</span> are required.
                        </p>
                        <InputCell
                            placeholder={"Survey Name"}
                            maxLength={MAX_SURVEY_NAME_LENGTH}
                            defaultValue={existingSurvey ? existingSurvey.name : ""}
                            required
                            id="surveyName"
                            label="Survey Name"
                            showRequiredIndicator
                            blurErrorMessage="Survey Name cannot be empty."
                            onChange={(e) => setSurveyName(e.target.value)}
                        />
                    </div>

                    {/* ---------- Web Submission (existing, with NEW Copy URL button) ---------- */}
                    <div className="tableSection sectionName">
                        <div className={"sectionTitle"}>
                            <div className={"title"}>Enable Web Submission</div>
                        </div>
                        <div onClick={() => setIsPublic(!isPublic)}
                             className={`tableCell switchCell ${isPublic ? 'switchOn' : 'switchOff'}`}
                             role="button"
                             aria-label="Toggle web submission"
                             aria-pressed={isPublic}
                             tabIndex={0}
                             onFocus={scrollIntoViewInsideModal}
                             onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsPublic(!isPublic); }}>
                            <div className="title">Enable</div>
                            <div className={`accessory accessorySwitch ${isPublic ? 'on' : 'off'}`}>
                                <div className="switchThumb" />
                            </div>
                        </div>

                        {surveyUrl && isPublic ? (
                            <>
                                <div className="sectionTitle">
                                    <p className="cellFootnote">
                                        You can access the public survey at{' '}
                                        <a style={{ cursor: 'pointer' }} target="_blank" rel="noreferrer" href={surveyUrl}>{surveyUrl}</a>
                                    </p>
                                </div>
                                {/* NEW: Copy URL — clipboard icon + centered text */}
                                <div className="tableCell actionCell"
                                     onClick={handleCopyUrl}
                                     role="button"
                                     aria-label="Copy public survey URL"
                                     tabIndex={0}
                                     onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCopyUrl(); }}>
                                    <FileCopyIcon className="actionCellIcon" />
                                    <div className="title">Copy URL</div>
                                </div>
                            </>
                        ) : null}
                    </div>

                    {/* ---------- QR Code section ---------- */}
                    <div className="tableSection sectionName">
                        <div className={"sectionTitle"}>
                            <div className={"title"}>QR Code</div>
                        </div>
                        <div onClick={() => handleQrToggle()}
                             className={`tableCell switchCell ${qrEnabled ? 'switchOn' : 'switchOff'}`}
                             role="switch"
                             aria-label="QR Code Enable"
                             aria-checked={qrEnabled}
                             tabIndex={0}
                             onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleQrToggle(); } }}>
                            <div className="title">Enable</div>
                            <div className={`accessory accessorySwitch ${qrEnabled ? 'on' : 'off'}`}>
                                <div className="switchThumb" />
                            </div>
                        </div>

                        {/* Start word + QR token inputs + action buttons — visible when toggle is ON */}
                        {qrEnabled ? (
                            <>
                                <div className="sectionTitle startWordSection">
                                    <p id="startWordLabel" className="startWordLabel">
                                        Text Message Start Word <span style={{ color: 'var(--colorDestructive, #c62828)' }} aria-hidden="true">*</span>
                                    </p>
                                    <InputCell
                                        placeholder={"Enter start word"}
                                        maxLength={MAX_START_WORD_LENGTH}
                                        defaultValue={startWord}
                                        onChange={handleStartWordChange}
                                        required
                                        id="surveyStartWord"
                                        inputProps={{
                                            'aria-labelledby': 'startWordLabel',
                                            'aria-required': 'true'
                                        }}
                                    />
                                </div>

                                <div className="sectionTitle startWordSection">
                                    <p id="qrTokenLabel" className="startWordLabel">
                                        QR Token
                                    </p>
                                    <InputCell
                                        placeholder={"Auto generated on save if left empty"}
                                        defaultValue={qrToken}
                                        onChange={handleQrTokenChange}
                                        id="surveyQrToken"
                                        inputProps={{
                                            'aria-labelledby': 'qrTokenLabel'
                                        }}
                                    />
                                </div>

                                {/* Regenerate — clickable only after survey saved with QR enabled */}
                                {canUseQrActions ? (
                                    <ConfirmModal
                                        title="Regenerate QR Code"
                                        text="Regenerating a QR Code will make all previously printed QR Codes Inactive. Are you sure you would like to continue?"
                                        confirmTitle="Yes"
                                        onConfirm={handleRegenerate}
                                        dialogClassName="regenerateQrModal"
                                    >
                                        <div className="tableCell actionCell"
                                             role="button"
                                             aria-label="Regenerate QR Code"
                                             tabIndex={0}
                                             onFocus={scrollIntoViewInsideModal}
                                             onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}>
                                            <CachedIcon className="actionCellIcon" />
                                            <div className="title">Regenerate QR Code</div>
                                        </div>
                                    </ConfirmModal>
                                ) : (
                                    <div className="tableCell actionCell disabled"
                                         role="button"
                                         aria-disabled={true}
                                         aria-label="Regenerate QR Code"
                                         tabIndex={-1}
                                         onFocus={scrollIntoViewInsideModal}>
                                        <CachedIcon className="actionCellIcon" />
                                        <div className="title">Regenerate QR Code</div>
                                    </div>
                                )}

                                {/* Download — clickable only after survey saved with QR enabled */}
                                {canUseQrActions ? (
                                    <FileDownload path={`/api/v1/survey/${existingSurvey.id}/qrcode`}>
                                        <div className="tableCell actionCell"
                                             role="button"
                                             aria-label="Download QR Code"
                                             tabIndex={0}
                                             onFocus={scrollIntoViewInsideModal}
                                             onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}>
                                            <GetAppIcon className="actionCellIcon" />
                                            <div className="title">Download QR Code</div>
                                        </div>
                                    </FileDownload>
                                ) : (
                                    <div className="tableCell actionCell disabled"
                                         role="button"
                                         aria-disabled={true}
                                         aria-label="Download QR Code"
                                         tabIndex={-1}
                                         onFocus={scrollIntoViewInsideModal}>
                                        <GetAppIcon className="actionCellIcon" />
                                        <div className="title">Download QR Code</div>
                                    </div>
                                )}
                            </>
                        ) : null}
                    </div>
                </div>
            </div>

            <div className="actionBar">
                <div className="actions">
                    <div onClick={onCancel}
                         className="button dismiss medium"
                         role="button"
                         aria-label="Cancel"
                         tabIndex={0}
                         onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onCancel(); }}>
                        <div className="title">Cancel</div>
                    </div>
                    <div onClick={isDoneDisabled ? undefined : saveSurvey}
                         className={`button confirm medium${isDoneDisabled ? ' disabled' : ''}`}
                         role="button"
                         aria-label="Done"
                         aria-disabled={isDoneDisabled}
                         tabIndex={isDoneDisabled ? -1 : 0}
                         onKeyDown={(e) => { if (!isDoneDisabled && (e.key === 'Enter' || e.key === ' ')) saveSurvey(); }}>
                        <div className="title">Done</div>
                    </div>
                </div>

                <div className="separator"></div>
            </div>
        </div>
    );
}
